import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, BookOpen, Clock, MapPin, Video, Users, CheckCircle2, ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { buildWhatsAppUrl, trackWhatsAppClick } from '@/lib/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Fallback tutor data for offline/error scenarios
const FALLBACK_TUTORS = [
  {
    id: 'fallback-1',
    fullName: 'Expert IELTS Tutor',
    subjects: ['IELTS', 'English'],
    profileBio: 'Experienced IELTS instructor with proven track record',
    yearsExperience: 5,
    tutoringMode: 'Both',
    isActive: true
  },
  {
    id: 'fallback-2',
    fullName: 'VCE Specialist',
    subjects: ['Mathematical Methods', 'Chemistry', 'Physics'],
    profileBio: 'VCE expert specializing in science and mathematics',
    yearsExperience: 4,
    tutoringMode: 'Online',
    isActive: true
  }
];

const TutorDirectoryPage = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('All');
  const [filterSubject, setFilterSubject] = useState('All');

  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  const fetchTutors = useCallback(async (isRetry = false) => {
    try {
      if (!BACKEND_URL) {
        throw new Error('Backend URL not configured');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000); // Extended to 45s for very slow connections

      const response = await fetch(`${BACKEND_URL}/api/public/tutors`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit'
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Filter out test data from public view
        const allTutors = data.tutors || [];
        const realTutors = allTutors.filter(tutor => 
          !tutor.fullName?.startsWith('TEST_') && 
          !tutor.fullName?.startsWith('Test ') &&
          tutor.isPubliclyVisible !== false
        );
        setTutors(realTutors);
        setRetryCount(0); // Reset retry count on success
        
        if (!isRetry && (!realTutors || realTutors.length === 0)) {
          toast.info('No tutors available yet. Our team is setting up the directory.');
        }
      } else {
        throw new Error(data.message || 'Invalid response from server');
      }
    } catch (error) {
      // Production error handling - no console logs
      
      // Auto-retry logic with exponential backoff
      if (retryCount < MAX_RETRIES && !isRetry) {
        setRetryCount(prev => prev + 1);
        const retryDelay = 4000 * (retryCount + 1); // Exponential backoff: 4s, 8s
        toast.info(`Connection issue. Retrying... (${retryCount + 1}/${MAX_RETRIES})`, {
          duration: retryDelay
        });
        setTimeout(() => fetchTutors(true), retryDelay);
      } else {
        // Final failure - show fallback data silently (real tutors will sign up soon)
        setTutors(FALLBACK_TUTORS);
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]); // Dependencies: retryCount (MAX_RETRIES is constant, BACKEND_URL is constant, FALLBACK_TUTORS is constant)

  const filterTutorList = useCallback(() => {
    let filtered = [...tutors];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(tutor =>
        tutor.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tutor.subjects?.some(sub => sub.toLowerCase().includes(searchTerm.toLowerCase())) ||
        tutor.profileBio?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Mode filter
    if (filterMode !== 'All') {
      filtered = filtered.filter(tutor =>
        tutor.tutoringMode?.includes(filterMode) || tutor.tutoringMode === 'Both'
      );
    }

    // Subject filter
    if (filterSubject !== 'All') {
      filtered = filtered.filter(tutor =>
        tutor.subjects?.includes(filterSubject)
      );
    }

    setFilteredTutors(filtered);
  }, [tutors, searchTerm, filterMode, filterSubject]);

  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);

  useEffect(() => {
    filterTutorList();
  }, [filterTutorList]);

  // Failsafe: prevent infinite loading
  useEffect(() => {
    if (!loading) return;
    
    const timer = setTimeout(() => {
      setLoading(false);
      toast.error('Request timed out. Please refresh.', {
        action: {
          label: 'Refresh',
          onClick: () => window.location.reload()
        }
      });
    }, 60000);
    return () => clearTimeout(timer);
  }, [loading]); // All dependencies included

  const getAllSubjects = () => {
    const subjects = new Set();
    tutors.forEach(tutor => {
      tutor.subjects?.forEach(sub => subjects.add(sub));
    });
    return Array.from(subjects).sort();
  };

  const getTutorInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTutorBadge = (tutor) => {
    if (tutor.tagline) return tutor.tagline;
    if (tutor.yearsExperience && tutor.yearsExperience >= 3) return 'Experienced Tutor';
    if (tutor.subjects?.includes('English')) return 'VCE Specialist';
    if (tutor.subjects?.includes('IELTS')) return 'IELTS Support';
    return 'Qualified Tutor';
  };

  const handleRequestTutor = (tutorId) => {
    navigate(`/get-started?tutor=${tutorId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
            style={{ borderColor: 'var(--accent-teal)' }}></div>
          <p style={{ color: 'var(--text-grey)' }}>Loading tutors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Navigation Bar */}
      <nav className="nav-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              <span className="hidden md:inline">Back to Home</span>
            </Button>
          </div>
          <Button onClick={() => navigate('/get-started')} className="btn-primary">
            Get Started
          </Button>
        </div>
      </nav>

      {/* Header */}
      <div className="premium-gradient text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="heading-xl mb-6">Meet Our Expert Tutors</h1>
            <p className="text-lg md:text-xl mb-8 opacity-100 font-medium">
              Choose from our hand-selected team of experienced VCE and IELTS tutors.
              All tutors are verified, qualified, and passionate about student success.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Verified Qualifications</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Experienced Teachers</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-semibold">Proven Results</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <Card className="premium-card p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Search Tutors
              </label>
              <Input
                placeholder="Search by name or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Tutoring Mode
              </label>
              <select
                className="w-full px-4 py-2 rounded-md border"
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
                style={{ borderColor: 'var(--border-color)' }}
              >
                <option value="All">All Modes</option>
                <option value="Online">Online Only</option>
                <option value="In-Person">In-Person Only</option>
                <option value="Both">Both</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Subject
              </label>
              <select
                className="w-full px-4 py-2 rounded-md border"
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                style={{ borderColor: 'var(--border-color)' }}
              >
                <option value="All">All Subjects</option>
                {getAllSubjects().map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      </div>

      {/* Tutors Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mb-6 text-center">
          <p className="text-base font-medium" style={{ color: 'var(--text-charcoal)' }}>
            Showing {filteredTutors.length} of {tutors.length} tutors
          </p>
        </div>

        {filteredTutors.length === 0 && tutors.length === 0 && !loading ? (
          <Card className="premium-card text-center py-16 px-8">
            <Users className="w-20 h-20 mx-auto mb-6" style={{ color: 'var(--accent-teal)', opacity: 0.3 }} />
            <h3 className="heading-lg mb-4">Our Tutor Directory is Being Prepared</h3>
            <p className="text-lg mb-6" style={{ color: 'var(--text-grey)' }}>
              We're currently setting up our expert tutors. Check back soon or contact us directly to get started!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => {
                  trackWhatsAppClick('tutor-directory-empty', 'find_tutor');
                  window.open(
                    buildWhatsAppUrl({ variant: 'find_tutor' }),
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
                className="premium-button"
                data-testid="directory-empty-whatsapp-btn"
              >
                Chat on WhatsApp
              </Button>
              <Button 
                onClick={() => navigate('/get-started')}
                className="premium-button-secondary"
              >
                Request a Tutor
              </Button>
              <Button 
                onClick={() => fetchTutors(false)}
                className="premium-button-secondary"
              >
                Refresh Page
              </Button>
            </div>
          </Card>
        ) : filteredTutors.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-grey)' }} />
            <h3 className="heading-md mb-2">No tutors match your search</h3>
            <p style={{ color: 'var(--text-grey)' }}>Try adjusting your search or filters</p>
            <Button 
              onClick={() => {
                setSearchTerm('');
                setFilterMode('All');
                setFilterSubject('All');
              }}
              className="premium-button-secondary mt-4"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutors.map((tutor) => (
              <Card key={tutor.id} className="premium-card overflow-hidden hover:shadow-lg transition-all">
                {/* Tutor Header */}
                <div className="p-6 text-center border-b" style={{ borderColor: 'var(--border-color)' }}>
                  {/* Avatar */}
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                    style={{ backgroundColor: 'var(--accent-teal)' }}>
                    {getTutorInitials(tutor.fullName)}
                  </div>
                  
                  <h3 className="heading-md mb-2">{tutor.fullName}</h3>
                  
                  {/* Badge */}
                  <span className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white mb-3"
                    style={{ backgroundColor: 'var(--accent-gold)' }}>
                    {getTutorBadge(tutor)}
                  </span>

                  {/* University */}
                  {tutor.university && (
                    <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                      {tutor.university}
                    </p>
                  )}
                </div>

                {/* Tutor Details */}
                <div className="p-6 space-y-4">
                  {/* Subjects */}
                  {tutor.subjects && tutor.subjects.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                        <span className="text-sm font-medium">Subjects</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {tutor.subjects.slice(0, 4).map((subject) => (
                          <span key={`${tutor.id}-${subject}`} className="px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: 'var(--bg-light)', color: 'var(--text-primary)' }}>
                            {subject}
                          </span>
                        ))}
                        {tutor.subjects.length > 4 && (
                          <span className="px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: 'var(--bg-light)', color: 'var(--text-grey)' }}>
                            +{tutor.subjects.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {tutor.profileBio && (
                    <p className="text-sm line-clamp-3" style={{ color: 'var(--text-grey)' }}>
                      {tutor.profileBio}
                    </p>
                  )}

                  {/* Experience */}
                  {tutor.yearsExperience > 0 && (
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                      <span className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        {tutor.yearsExperience} years experience
                      </span>
                    </div>
                  )}

                  {/* Mode */}
                  {tutor.tutoringMode && (
                    <div className="flex items-center gap-2">
                      {tutor.tutoringMode.includes('Online') ? (
                        <Video className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                      ) : (
                        <MapPin className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                      )}
                      <span className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        {tutor.tutoringMode}
                      </span>
                    </div>
                  )}

                  {/* Availability */}
                  {tutor.availability && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                      <span className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        {tutor.availability}
                      </span>
                    </div>
                  )}

                  {/* CTA */}
                  <Button
                    onClick={() => handleRequestTutor(tutor.id)}
                    className="w-full premium-button mt-4"
                  >
                    Request This Tutor
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Can't Find Section */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <Card className="premium-card p-8">
            <h3 className="heading-md mb-4">Can't find the right tutor?</h3>
            <p className="mb-6" style={{ color: 'var(--text-grey)' }}>
              Let us match you with the perfect tutor based on your specific needs and goals.
            </p>
            <Button
              onClick={() => navigate('/get-started')}
              className="premium-button-secondary"
            >
              Get Matched With a Tutor
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TutorDirectoryPage;
